import { Dialog } from "@headlessui/react";
import { Modal } from "../../UI/Modal/Modal.tsx";
import {
  HistoryAddedBlockDTO,
  HistoryCloserDetailDTO,
  HistoryPeriodSummaryDTO,
} from "../../HubSpot/useGetHubSpotCloserAvailabilityHistory.ts";
import {
  deltaClass,
  formatDelta,
  nameFromEmail,
  teamLabel,
} from "./closerAvailabilityHistoryStats.ts";
import { HistoryDayTable } from "./HistoryDayTable.tsx";
import { CloserSlotGrid } from "./CloserSlotGrid.tsx";
import { HistoryChangesBlocksList } from "./HistoryChangesBlocksList.tsx";

type Props = {
  closer: HistoryCloserDetailDTO | null;
  periods: HistoryPeriodSummaryDTO[];
  addedBlocks: HistoryAddedBlockDTO[] | null;
  hasClosure: boolean;
  comparable: boolean;
  onClose: () => void;
};

export function CloserHistoryDetailModal({
  closer,
  periods,
  addedBlocks,
  hasClosure,
  comparable,
  onClose,
}: Props) {
  const opening = closer?.opening?.effective ?? 0;
  const closure = closer?.closure?.effective ?? 0;
  const history = closer
    ? periods
        .filter((p) => p.openingTakenAt && p.closureTakenAt)
        .map((p) => {
          const c = p.closers.find((x) => x.email === closer.email);
          return c
            ? {
                periodStart: p.periodStart,
                delta:
                  (c.closure?.effective ?? 0) - (c.opening?.effective ?? 0),
              }
            : null;
        })
        .filter((x): x is { periodStart: string; delta: number } => x !== null)
    : [];

  return (
    <Modal
      isOpen={closer !== null}
      onClose={onClose}
      title={closer ? nameFromEmail(closer.email) : ""}
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="flex-1 md:flex-auto md:max-w-3xl transform overflow-hidden md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
            {title}
          </Dialog.Title>
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      {closer && (
        <div className="space-y-5">
          <p className="text-sm text-gray-500">
            {closer.email} · {teamLabel(closer.team)}
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-gray-500">Capacity apertura</p>
              <p className="text-2xl font-semibold tabular-nums">{opening}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-gray-500">Slot effettivi</p>
              <p className="text-2xl font-semibold tabular-nums">
                {hasClosure ? closure : "—"}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-gray-500">Δ</p>
              <p
                className={`text-2xl font-semibold tabular-nums ${comparable ? deltaClass(closure - opening) : "text-gray-300"}`}
              >
                {comparable ? formatDelta(closure - opening) : "—"}
              </p>
            </div>
          </div>

          {closer.days && (
            <HistoryDayTable
              days={closer.days}
              hasClosure={hasClosure}
              comparable={comparable}
            />
          )}

          {closer.slots && (
            <div className="space-y-2">
              <h4 className="font-semibold">Agenda slot per slot</h4>
              <CloserSlotGrid slots={closer.slots} />
            </div>
          )}

          {addedBlocks && (
            <div className="space-y-2">
              <h4 className="font-semibold">Blocchi aggiunti</h4>
              <HistoryChangesBlocksList
                blocks={addedBlocks.filter((b) => b.email === closer.email)}
              />
            </div>
          )}

          {history.length > 0 && (
            <p className="text-xs text-gray-500">
              Δ nei periodi precedenti:{" "}
              {history.map((h, i) => (
                <span key={h.periodStart}>
                  {i > 0 && " · "}
                  <span className={deltaClass(h.delta)}>
                    {formatDelta(h.delta)}
                  </span>
                </span>
              ))}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
